import { JSX, useCallback } from "react";
import Grid from "@mui/material/Grid";

import {
	InformationRight,
	ReportFieldRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import {
	BooleanField,
	DateField,
	DecimalField,
	Field,
	FieldType,
	GroupField,
	ImageField,
	IntegerField,
	MultiSelectField,
	RatingField,
	SelectField,
	TextField,
	UploadField,
	UrlField
} from "core/field/FieldService";
import FieldInput from "core/field/report/FieldInput";
import { ReportValues } from "./ReportService";

export type ReportFormProps = {
	structure: Field[];
	values: ReportValues;
	labels: Map<string, Array<string>>;
	filter: Array<string>;
	rights: ReportFieldRights;
	edit: boolean;

	onChange: (uuid: string, newValue: string) => void;
};

export default function ReportForm(
	props: Readonly<ReportFormProps>
): JSX.Element {
	function mergeFields(fields: Field[]): Field[] {
		return fields.map(mergeField);
	}

	function mergeField(field: Field): Field {
		switch (field.type) {
			case FieldType.BOOLEAN:
				(field as BooleanField).value =
					props.values[field.uuid] === "true";
				break;
			case FieldType.DATE:
				(field as DateField).value = props.values[field.uuid] ?? "";
				break;
			case FieldType.DECIMAL:
				(field as DecimalField).value = Number.parseFloat(
					props.values[field.uuid]
				);
				break;
			case FieldType.GROUP:
				(field as GroupField).children = mergeFields(
					(field as GroupField).children
				);
				break;
			case FieldType.IMAGE:
				(field as ImageField).value = JSON.parse(
					props.values[field.uuid] ?? "[]"
				);
				break;
			case FieldType.INTEGER:
				(field as IntegerField).value = Number.parseInt(
					props.values[field.uuid]
				);
				break;
			case FieldType.RATING:
				(field as RatingField).value = !Number.isNaN(
					Number.parseInt(props.values[field.uuid], 10)
				)
					? Number.parseInt(props.values[field.uuid], 10)
					: null;
				break;
			case FieldType.SELECT:
				(field as SelectField).value = props.values[field.uuid] ?? "";
				break;
			case FieldType.MULTI_SELECT:
				(field as MultiSelectField).value = props.values[field.uuid]
					? JSON.parse(props.values[field.uuid])
					: [];
				break;
			case FieldType.TEXT:
				(field as TextField).value = props.values[field.uuid] ?? "";
				break;
			case FieldType.URL:
				(field as UrlField).value = props.values[field.uuid] ?? "";
				break;
			case FieldType.UPLOAD:
				(field as UploadField).value = JSON.parse(
					props.values[field.uuid] ?? "[]"
				);
				break;
			default:
				break;
		}

		return field;
	}

	function toId(uuid: string): string {
		const separatorIndex = uuid.indexOf("#");
		return separatorIndex === -1 ? uuid : uuid.substring(0, separatorIndex);
	}

	function toCompetenceId(uuid: string): string | null {
		const separatorIndex = uuid.indexOf("#");
		return separatorIndex === -1
			? null
			: uuid.substring(separatorIndex + 1);
	}

	const isShown = useCallback(
		(field: Field): boolean => {
			if (props.filter.length === 0) {
				return true;
			}

			const competenceId = toCompetenceId(field.uuid);
			if (competenceId === null) {
				return true;
			}

			return (
				props.labels
					.get(competenceId)
					?.some(label => props.filter.includes(label)) ?? false
			);
		},
		[props.labels, props.filter]
	);

	return (
		<Grid id="form" container rowSpacing={2} columnSpacing={4}>
			{props.structure
				.filter(field => props.rights?.[toId(field.uuid)] !== undefined)
				.filter(
					field =>
						props.rights[toId(field.uuid)][
							InformationRight.HIDDEN
						] !== true
				)
				.filter(isShown)
				.map(field => (
					<Grid key={field.uuid} item xs={12}>
						<FieldInput
							labels={props.labels}
							filter={props.filter}
							field={mergeField(field)}
							editable={props.edit}
							rights={props.rights}
							onChange={props.onChange}
						/>
					</Grid>
				))}
		</Grid>
	);
}
